import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { AiSuggestions } from '../components/procurement/AiSuggestions';
import { PageHeader } from '../components/design-system';
import { toast } from 'sonner';
import {
  Gauge, ChevronDown, ChevronUp, ShoppingCart, ClipboardList, Sparkles,
  Settings2, HelpCircle, RefreshCw, AlertTriangle, Clock, CheckCircle2, Hourglass, Search, XCircle
} from 'lucide-react';

const URGENCY_STYLE = {
  alta: 'bg-red-100 text-red-700 border-red-200',
  'média': 'bg-amber-100 text-amber-700 border-amber-200',
  baixa: 'bg-slate-100 text-slate-600 border-slate-200',
};

const ACTION_META = {
  encomendar_hoje: { label: 'Encomendar hoje', icon: AlertTriangle, cls: 'bg-red-50 border-red-200 text-red-800' },
  encomendar_em_breve: { label: 'Encomendar em breve', icon: Clock, cls: 'bg-amber-50 border-amber-200 text-amber-800' },
  aguardar: { label: 'Aguardar', icon: Hourglass, cls: 'bg-blue-50 border-blue-200 text-blue-800' },
  pode_aguardar: { label: 'Pode aguardar', icon: CheckCircle2, cls: 'bg-slate-50 border-slate-200 text-slate-700' },
};

const fmtEur = (v) => `${(v || 0).toLocaleString('pt-PT', { maximumFractionDigits: 0 })}€`;

function TriBadge({ label, met }) {
  if (met === null || met === undefined)
    return <Badge variant="outline" className="text-slate-400 text-[10px]">{label}: —</Badge>;
  return met
    ? <Badge className="bg-green-100 text-green-700 text-[10px]">✓ {label}</Badge>
    : <Badge variant="outline" className="text-orange-600 border-orange-200 text-[10px]">✗ {label}</Badge>;
}

export default function CockpitCompras() {
  const navigate = useNavigate();
  const [data, setData] = useState({ cards: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState({});
  const [whySku, setWhySku] = useState(null);
  const [whyData, setWhyData] = useState(null);
  const [whyProductId, setWhyProductId] = useState(null);
  const [cfgCard, setCfgCard] = useState(null);
  const [cfgForm, setCfgForm] = useState({});
  const [pendingSuggestions, setPendingSuggestions] = useState(0);

  useEffect(() => {
    axios.get(`${API}/procurement/suggestions?status=pending&limit=1`)
      .then((res) => setPendingSuggestions(res.data?.counts?.pending || 0))
      .catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/procurement/cockpit`);
      setData(res.data);
    } catch (e) {
      toast.error('Erro ao carregar o cockpit');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addAiToCart = async (card) => {
    setBusy((b) => ({ ...b, [card.sage_supplier_code]: true }));
    try {
      const res = await axios.post(`${API}/procurement/cockpit/supplier/${card.sage_supplier_code}/add-ai-to-cart`);
      toast.success(res.data?.message || 'Sugestões adicionadas ao carrinho');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao adicionar ao carrinho');
    } finally {
      setBusy((b) => ({ ...b, [card.sage_supplier_code]: false }));
    }
  };

  const createOrder = async (card) => {
    setBusy((b) => ({ ...b, [card.sage_supplier_code]: true }));
    try {
      const res = await axios.post(`${API}/shopping-cart/create-orders?supplier=${encodeURIComponent(card.supplier_name)}`);
      const n = res.data?.orders_created?.length ?? res.data?.total_orders ?? 1;
      toast.success(`Encomenda criada (${n}) para ${card.supplier_name}`);
      navigate('/purchase-orders');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao criar encomenda');
    } finally {
      setBusy((b) => ({ ...b, [card.sage_supplier_code]: false }));
    }
  };

  const openWhy = async (sku, productId) => {
    setWhySku(sku);
    setWhyData(null);
    setWhyProductId(productId || null);
    try {
      const res = await axios.get(`${API}/procurement/intelligence/params/${sku}`);
      setWhyData(res.data);
    } catch (e) {
      setWhyData({ error: e.response?.data?.detail || 'Sem análise disponível para este artigo.' });
    }
  };

  const openCfg = (card) => {
    setCfgCard(card);
    setCfgForm({
      global_moq: card.moq ?? '',
      min_order_value: card.min_order_value ?? '',
      free_shipping_value: card.free_shipping_value ?? '',
      lead_time_days_avg: card.lead_time_days ?? '',
    });
  };

  const saveCfg = async () => {
    try {
      const payload = {};
      for (const [k, v] of Object.entries(cfgForm)) {
        if (v !== '' && v !== null && !Number.isNaN(Number(v))) payload[k] = Number(v);
      }
      await axios.patch(`${API}/procurement/cockpit/supplier/${cfgCard.sage_supplier_code}`, payload);
      toast.success('Configuração do fornecedor guardada');
      setCfgCard(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao guardar configuração');
    }
  };

  const deactivateProductAI = async (sku, productName) => {
    if (!window.confirm(`Desactivar Procurement AI para "${productName}" (${sku})?\n\nO produto deixará de aparecer nas sugestões de compra.`)) {
      return;
    }
    
    try {
      setBusy((b) => ({ ...b, [sku]: true }));
      await axios.post(`${API}/admin/procurement-ai/deactivate-product/${encodeURIComponent(sku)}`);
      toast.success(`Procurement AI desactivado para ${sku}`);
      fetchData(); // Refresh the cockpit data
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao desactivar Procurement AI');
    } finally {
      setBusy((b) => ({ ...b, [sku]: false }));
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 pb-8">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
          <div className="animate-pulse">
            <div className="h-8 w-64 bg-slate-200 rounded mb-2"></div>
            <div className="h-4 w-96 bg-slate-100 rounded"></div>
          </div>
        </div>
      </div>
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <div className="animate-pulse space-y-4">
          <div className="h-12 bg-slate-100 rounded"></div>
          <div className="h-32 bg-slate-100 rounded"></div>
          <div className="h-32 bg-slate-100 rounded"></div>
        </div>
      </div>
    </div>
  );

  const t = data.totals || {};
  const q = search.trim().toLowerCase();
  const visibleCards = !q ? data.cards : data.cards.filter(c =>
    (c.supplier_name || '').toLowerCase().includes(q)
    || (c.sage_supplier_code || '').toLowerCase().includes(q)
    || [...(c.cart_items || []), ...(c.ai_pending || []), ...(c.upcoming || [])].some(i =>
      (i.sku || '').toLowerCase().includes(q) || (i.name || '').toLowerCase().includes(q))
  );
  
  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="cockpit-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4 md:py-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Gauge className="h-5 w-5 md:h-6 md:w-6 text-red-600" />
                Cockpit de Compras
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                {t.suppliers || 0} fornecedores com necessidades ·{' '}
                <span className="font-semibold text-red-600">{t.order_today || 0} para encomendar hoje</span>{' '}
                · valor estimado {fmtEur(t.total_estimated_value)}
              </p>
            </div>
            <Button 
              variant="outline" 
              onClick={() => { setLoading(true); fetchData(); }} 
              data-testid="cockpit-refresh"
              className="shrink-0"
            >
              <RefreshCw className="h-4 w-4 mr-2" /> Atualizar
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4 md:py-6">
        <Tabs defaultValue="fornecedores">
          <TabsList data-testid="cockpit-tabs" className="mb-4">
            <TabsTrigger value="fornecedores" data-testid="tab-fornecedores">Fornecedores</TabsTrigger>
            <TabsTrigger value="sugestoes" data-testid="tab-sugestoes" className="gap-1.5">
              <Sparkles className="h-3.5 w-3.5" /> Sugestões AI
              {pendingSuggestions > 0 && (
                <Badge className="bg-violet-600 text-white text-[10px] px-1.5 h-4 min-w-4 justify-center">
                  {pendingSuggestions > 99 ? '99+' : pendingSuggestions}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="fornecedores" className="space-y-4 mt-0">
            {/* Search */}
            <div className="relative max-w-md" data-testid="cockpit-search-wrap">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Pesquisar fornecedor, código ou artigo..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="cockpit-search-input"
              />
            </div>

            {visibleCards.length === 0 && (
              <Card className="border-slate-200">
                <CardContent className="py-12 text-center text-slate-500">
                  {search ? 'Nenhum fornecedor ou artigo corresponde à pesquisa.' : 'Sem necessidades de compra neste momento. 👌'}
                </CardContent>
              </Card>
            )}

            <div className="space-y-3">
        {visibleCards.map((card) => {
          const key = card.sage_supplier_code || card.supplier_name;
          const am = ACTION_META[card.suggested_action] || ACTION_META.pode_aguardar;
          const Icon = am.icon;
          const isOpen = expanded[key];
          return (
            <Card key={key} className="border-slate-200" data-testid={`supplier-card-${key}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-slate-800 truncate max-w-[420px]">{card.supplier_name}</h3>
                      <Badge className={`${URGENCY_STYLE[card.urgency]} border text-[10px]`}>
                        urgência {card.urgency}
                      </Badge>
                      {card.cart_origins.map((o) => (
                        <Badge key={o} variant="outline" className="text-[10px] text-slate-500">{o}</Badge>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {card.total_items} artigos ({card.cart_count} no carrinho · {card.ai_pending_count} sugestões AI)
                      {' '}· lead time {card.lead_time_days}d
                      {card.upcoming_count > 0 && ` · +${card.upcoming_count} previstos em ${card.upcoming_days}d`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-slate-800">{fmtEur(card.total_estimated_value)}</p>
                    <div className="flex gap-1 justify-end mt-1">
                      <TriBadge label="MOQ" met={card.moq_met} />
                      <TriBadge label="Portes" met={card.free_shipping_met} />
                    </div>
                  </div>
                </div>

                <div className={`flex items-start gap-2 rounded-md border px-3 py-2 ${am.cls}`} data-testid={`action-${key}`}>
                  <Icon className="h-4 w-4 mt-0.5 shrink-0" />
                  <div className="text-sm">
                    <span className="font-semibold">{am.label}.</span>{' '}
                    <span className="opacity-90">{card.suggested_reason}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {card.ai_pending_count > 0 && (
                    <Button size="sm" onClick={() => addAiToCart(card)} disabled={busy[key]}
                            data-testid={`add-ai-${key}`}>
                      <ShoppingCart className="h-4 w-4 mr-1" />
                      Adicionar {card.ai_pending_count} {card.ai_pending_count === 1 ? 'sugestão' : 'sugestões'} ao carrinho
                    </Button>
                  )}
                  {card.cart_count > 0 && (
                    <Button size="sm" variant="default" className="bg-slate-800 hover:bg-slate-700"
                            onClick={() => createOrder(card)} disabled={busy[key]}
                            data-testid={`create-order-${key}`}>
                      <ClipboardList className="h-4 w-4 mr-1" /> Criar encomenda ({card.cart_count})
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openCfg(card)} data-testid={`cfg-${key}`}>
                    <Settings2 className="h-4 w-4 mr-1" /> Configurar
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setExpanded((e) => ({ ...e, [key]: !e[key] }))}
                          data-testid={`expand-${key}`}>
                    {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    {isOpen ? 'Fechar' : 'Ver artigos'}
                  </Button>
                </div>

                {isOpen && (
                  <div className="overflow-x-auto border rounded-md">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-xs text-slate-500">
                        <tr>
                          <th className="text-left py-2 px-3">SKU</th>
                          <th className="text-left py-2 px-3">Artigo</th>
                          <th className="text-left py-2 px-3">Origem</th>
                          <th className="text-right py-2 px-3">Stock</th>
                          <th className="text-right py-2 px-3">ROP</th>
                          <th className="text-right py-2 px-3">Qtd</th>
                          <th className="text-right py-2 px-3">Valor</th>
                          <th className="py-2 px-3"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {card.cart_items.map((i) => (
                          <tr key={i.id} className="border-t">
                            <td className="py-1.5 px-3 font-mono text-xs">{i.sku}</td>
                            <td className="py-1.5 px-3">{i.product_name}</td>
                            <td className="py-1.5 px-3"><Badge variant="secondary" className="text-[10px]">{i.origin_label}</Badge></td>
                            <td className="py-1.5 px-3 text-right">—</td>
                            <td className="py-1.5 px-3 text-right">—</td>
                            <td className="py-1.5 px-3 text-right">{i.quantity}</td>
                            <td className="py-1.5 px-3 text-right">{fmtEur(i.quantity * i.unit_price)}</td>
                            <td className="py-1.5 px-3 text-right">
                              <div className="flex items-center justify-end gap-0.5">
                                {/* Show deactivate button only for AI-originated items */}
                                {i.origin_label?.toLowerCase().includes('ai') && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => deactivateProductAI(i.sku, i.product_name)}
                                    disabled={busy[i.sku]}
                                    title="Desactivar Procurement AI para este produto"
                                    data-testid={`deactivate-ai-cart-${i.sku}`}
                                  >
                                    <XCircle className="h-4 w-4 text-red-400 hover:text-red-600" />
                                  </Button>
                                )}
                                <Button variant="ghost" size="sm" onClick={() => openWhy(i.sku, i.product_id)} data-testid={`why-${i.sku}`}>
                                  <HelpCircle className="h-4 w-4 text-slate-400" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {card.ai_pending.map((n) => (
                          <tr key={n.sku} className="border-t bg-amber-50/40">
                            <td className="py-1.5 px-3 font-mono text-xs">{n.sku}</td>
                            <td className="py-1.5 px-3">
                              {n.name}
                              {(n.on_order || 0) > 0 && (
                                <span
                                  className={`ml-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-medium whitespace-nowrap ${n.on_order_pos?.length ? 'cursor-pointer hover:bg-blue-200 underline decoration-dotted' : ''}`}
                                  title={n.on_order_pos?.length
                                    ? `${n.on_order} unidade(s) por receber em: ${n.on_order_pos.join(', ')} — clique para abrir`
                                    : `${n.on_order} unidade(s) já encomendada(s) e por receber — a sugestão mostra apenas o que falta`}
                                  onClick={(e) => {
                                    if (!n.on_order_pos?.length) return;
                                    e.stopPropagation();
                                    navigate(`/purchase-orders?po=${encodeURIComponent(n.on_order_pos[0])}`);
                                  }}
                                  data-testid={`on-order-chip-${n.sku}`}
                                >
                                  🚚 {n.on_order} a caminho
                                </span>
                              )}
                            </td>
                            <td className="py-1.5 px-3"><Badge className="bg-amber-100 text-amber-700 text-[10px]">AI · por adicionar</Badge></td>
                            <td className="py-1.5 px-3 text-right">{n.current_stock}</td>
                            <td className="py-1.5 px-3 text-right">{n.reorder_point}</td>
                            <td className="py-1.5 px-3 text-right" data-testid={`ai-need-qty-${n.sku}`}>
                              {n.suggested_quantity}
                              {n.purchase_unit_factor > 1 && (
                                <div className="text-[10px] text-slate-500" title={`Necessidade ${n.need_base_units} UN, arredondada a caixas completas`}>
                                  {n.suggested_packs} × {n.purchase_unit}
                                </div>
                              )}
                            </td>
                            <td className="py-1.5 px-3 text-right">{fmtEur(n.estimated_value)}</td>
                            <td className="py-1.5 px-3 text-right">
                              <div className="flex items-center justify-end gap-0.5">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => deactivateProductAI(n.sku, n.name)}
                                  disabled={busy[n.sku]}
                                  title="Desactivar Procurement AI para este produto"
                                  data-testid={`deactivate-ai-${n.sku}`}
                                >
                                  <XCircle className="h-4 w-4 text-red-400 hover:text-red-600" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => openWhy(n.sku, n.product_id)} data-testid={`why-${n.sku}`}>
                                  <HelpCircle className="h-4 w-4 text-slate-400" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
          </div>
          </TabsContent>

          <TabsContent value="sugestoes" className="mt-0">
            <AiSuggestions onCountChange={setPendingSuggestions} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal "Porquê?" */}
      <Dialog open={!!whySku} onOpenChange={(o) => !o && setWhySku(null)}>
        <DialogContent className="max-w-lg" data-testid="why-modal">
          <DialogHeader>
            <DialogTitle>Porquê? · {whySku}</DialogTitle>
            <DialogDescription>Explicação da sugestão do Procurement Intelligence</DialogDescription>
          </DialogHeader>
          {!whyData ? (
            <p className="text-sm text-slate-500">A carregar análise...</p>
          ) : whyData.error ? (
            <p className="text-sm text-slate-500">{whyData.error}</p>
          ) : (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-slate-50 rounded p-2">Classe ABC: <strong>{whyData.abc_class}</strong></div>
                <div className="bg-slate-50 rounded p-2">Confiança: <strong>{whyData.confidence?.label}</strong> ({whyData.confidence?.score})</div>
                <div className="bg-slate-50 rounded p-2">Consumo médio: <strong>{whyData.avg_daily_qty?.toFixed(2)}/dia</strong></div>
                <div className="bg-slate-50 rounded p-2">Consumo mensal: <strong>{Math.round(whyData.avg_monthly_qty || 0)}</strong></div>
                <div className="bg-slate-50 rounded p-2">Reorder point: <strong>{whyData.reorder_point}</strong></div>
                <div className="bg-slate-50 rounded p-2">Safety stock: <strong>{whyData.safety_stock}</strong></div>
                <div className="bg-slate-50 rounded p-2">Lead time usado: <strong>{whyData.lead_time_days}d</strong></div>
                <div className="bg-slate-50 rounded p-2">Sazonal: <strong>{whyData.seasonal ? 'Sim' : 'Não'}</strong></div>
              </div>
              <div className="text-xs text-slate-500 space-y-1">
                <p><strong>Fórmula:</strong> ROP = consumo diário × lead time + safety stock (z·σ·√LT)</p>
                <p><strong>Dados analisados:</strong> {whyData.explain?.docs_analyzed} documentos de venda reais
                  {' '}({whyData.explain?.period?.start} → {whyData.explain?.period?.end})</p>
                <p><strong>Fatores ignorados:</strong> movimentos internos (TAR/GR/ajustes) — só procura real conta.</p>
                <p><strong>Última análise:</strong> {whyData.explain?.computed_at?.slice(0, 16).replace('T', ' ')}</p>
              </div>
              {whyProductId && (
                <Button variant="outline" size="sm" className="w-full" data-testid="why-open-ficha"
                        onClick={() => navigate(`/inventory/${whyProductId}`)}>
                  Ver ficha de procurement completa
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal config fornecedor */}
      <Dialog open={!!cfgCard} onOpenChange={(o) => !o && setCfgCard(null)}>
        <DialogContent className="max-w-md" data-testid="cfg-modal">
          <DialogHeader>
            <DialogTitle>Configurar · {cfgCard?.supplier_name}</DialogTitle>
            <DialogDescription>Estes valores alimentam as decisões do cockpit (prioridade manual)</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {[
              ['global_moq', 'MOQ (quantidade mínima de encomenda)'],
              ['min_order_value', 'Valor mínimo de encomenda (€)'],
              ['free_shipping_value', 'Portes grátis a partir de (€)'],
              ['lead_time_days_avg', 'Lead time médio (dias)'],
            ].map(([k, label]) => (
              <div key={k}>
                <Label className="text-xs">{label}</Label>
                <Input type="number" value={cfgForm[k]} data-testid={`cfg-${k}`}
                       onChange={(e) => setCfgForm((f) => ({ ...f, [k]: e.target.value }))} />
              </div>
            ))}
            <Button className="w-full" onClick={saveCfg} data-testid="cfg-save">Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
